import { useState, useRef, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import { UploadCloud, FileText, Loader2, Sparkles, X } from "lucide-react";
import { toast } from "sonner";
import { uploadDocument } from "../lib/api";
import { logError } from "../lib/log";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
} from "../components/ui/dialog";
import { Button } from "../components/ui/button";
import { tr } from "../lib/i18n";

const ACCEPT = ".pdf,.png,.jpg,.jpeg,.webp,.gif,.heic,.heif,.tif,.tiff,.txt,.md,.csv,.json,.doc,.docx,.xls,.xlsx,.ppt,.pptx,.rtf,.eml,.mp3,.m4a,.wav,.aac,.ogg,.mp4,.mov,.webm";
const fmtMB = (b) => `${(b / 1048576).toFixed(b > 100 * 1048576 ? 0 : 1)} MB`;

export const UploadDropzoneModal = ({ open, onClose, onUploaded }) => {
  const [dragging, setDragging] = useState(false);
  const [busy, setBusy] = useState(false);
  const [current, setCurrent] = useState("");
  const [progress, setProgress] = useState(null);
  const navigate = useNavigate();
  const inputRef = useRef(null);

  const handleFiles = useCallback(
    async (files) => {
      const list = Array.from(files);
      if (!list.length) return;
      setBusy(true);
      let ok = 0;
      for (const f of list) {
        setCurrent(f.name);
        setProgress(f.size > 20 * 1048576 ? { pct: 0, size: f.size } : null);
        try {
          const doc = await uploadDocument(f, "upload", (p) => setProgress((prev) => (prev ? { ...prev, pct: p } : prev)));
          ok += 1;
          const estate = ["wills", "legal"].includes(doc.category) || /will|trust|power of attorney|directive|deed/i.test(doc.title || "");
          toast.success(`Filed under ${doc.category}`, { description: doc.title, ...(estate ? { action: { label: "Add to Library", onClick: () => navigate("/library#library-documents") }, duration: 8000 } : {}) });
          onUploaded?.(doc);
        } catch (e) {
          const st = e?.response?.status;
          const detail = e?.response?.data?.detail;
          const msg = st === 401 ? "Your vault locked while uploading — unlock and try again"
            : typeof detail === "string" ? detail
            : st === 413 ? `${f.name} is too large (limit 250 MB)`
            : st >= 500 ? `The server couldn't process ${f.name} (error ${st}) — please try again`
            : !e?.response ? `Upload of ${f.name} was interrupted — check your connection and try again`
            : `Failed to process ${f.name}`;
          toast.error(msg, { duration: 8000 });
          logError("upload failed", e);
        }
      }
      setBusy(false);
      setCurrent("");
      setProgress(null);
      if (ok > 0) onClose();
    },
    [onUploaded, onClose, navigate]
  );

  const onDrop = (e) => {
    e.preventDefault();
    setDragging(false);
    if (busy) return;
    handleFiles(e.dataTransfer.files);
  };

  return (
    <Dialog open={open} onOpenChange={(v) => !v && !busy && onClose()}>
      <DialogContent data-testid="document-upload-modal" className="sm:max-w-lg">
        <DialogHeader>
          <DialogTitle className="font-serif text-2xl">{tr("Add to your vault")}</DialogTitle>
          <DialogDescription>
            {tr("Drop a document and our AI reads it, extracts the key details, and files it in the right place automatically.")}
          </DialogDescription>
        </DialogHeader>

        {busy ? (
          <div className="py-10 flex flex-col items-center text-center">
            <div className="relative h-16 w-16 grid place-items-center">
              <div className="absolute inset-0 rounded-2xl ai-shimmer" />
              <Sparkles className="h-7 w-7 text-primary relative" />
            </div>
            <p className="mt-5 font-medium flex items-center gap-2">
              <Loader2 className="h-4 w-4 animate-spin" /> {progress ? (progress.pct < 1 ? "Uploading large file…" : "Storing…") : "Reading & filing…"}
            </p>
            <p className="mt-1 text-sm text-muted-foreground font-mono truncate max-w-full">
              {current}
            </p>
            {progress && (
              <div className="mt-4 w-full max-w-xs" data-testid="upload-progress">
                <div className="h-1.5 rounded-full bg-muted overflow-hidden"><div className="h-full bg-primary transition-[width]" style={{ width: `${Math.round(progress.pct * 100)}%` }} /></div>
                <div className="mt-1 text-[11px] text-muted-foreground">{Math.round(progress.pct * 100)}% · {fmtMB(progress.size)} in 5 MB pieces</div>
              </div>
            )}
          </div>
        ) : (
          <div
            data-testid="dropzone-area"
            onDragOver={(e) => {
              e.preventDefault();
              setDragging(true);
            }}
            onDragLeave={() => setDragging(false)}
            onDrop={onDrop}
            onClick={() => inputRef.current?.click()}
            className={`cursor-pointer rounded-2xl border-2 border-dashed p-10 text-center transition-colors ${
              dragging ? "border-primary bg-primary/5" : "border-border bg-muted/30 hover:bg-muted/50"
            }`}
          >
            <div className="h-14 w-14 mx-auto rounded-2xl bg-primary/10 text-primary grid place-items-center">
              <UploadCloud className="h-7 w-7" />
            </div>
            <p className="mt-4 font-medium"><span className="hidden sm:inline">{tr("Drag & drop or click to browse")}</span><span className="sm:hidden">{tr("Tap to choose files or take a photo")}</span></p>
            <p className="mt-1 text-sm text-muted-foreground">
              PDF, photos, Office files, audio & video · up to 250 MB each · several at once
            </p>
            <input
              ref={inputRef}
              data-testid="file-input"
              type="file"
              multiple
              accept={ACCEPT}
              className="hidden"
              onChange={(e) => handleFiles(e.target.files)}
            />
          </div>
        )}

        {!busy && (
          <div className="flex items-center gap-2 text-xs text-muted-foreground">
            <FileText className="h-3.5 w-3.5" />
            {tr("Everything stays in your family's private vault.")}
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
};
